var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, Box, Card, CardMedia, CardActionArea, Typography, } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { buildFieldValidation } from "../../validators/field-components";
import CommentArea from "../common/CommentArea";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { getElementValueKey, reorderChoices } from "../../../../utils/helpers";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import { useChoicesConditions } from "../../hooks/useChoicesConditions";
var ImagePickerField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormContext(), unregister = _b.unregister, getValues = _b.getValues, setValue = _b.setValue;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _c = useElementConditions(element), isVisible = _c.isVisible, isEnabled = _c.isEnabled, isInteractive = _c.isInteractive, fieldRequired = _c.isRequired;
    var _d = useChoicesConditions(element), choicesVisible = _d.choicesVisible, choicesEnabled = _d.choicesEnabled;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var _e = React.useState(new Set()), playingVideos = _e[0], setPlayingVideos = _e[1];
    var videoRefs = useRef({});
    var initialValue = getInitialValueForElement(element);
    var contentMode = element.contentMode;
    var choices = useMemo(function () {
        var choicesFormatted = (element.choices || []);
        choicesFormatted = choicesFormatted.map(function (c) { return (__assign(__assign({}, c), { text: c.text || c.value })); });
        if (element.choicesOrder) {
            choicesFormatted = reorderChoices(choicesFormatted, element.choicesOrder);
        }
        return choicesFormatted;
    }, [element]);
    useEffect(function () {
        if (!choicesVisible) {
            var currentValue = getValues(valueKey);
            var isEmpty = Array.isArray(currentValue)
                ? currentValue.length === 0
                : !currentValue;
            if (!isEmpty) {
                setValue(valueKey, element.multiSelect ? [] : "");
            }
        }
    }, [choicesVisible, valueKey, element.multiSelect, getValues, setValue]);
    var handleImageClick = useCallback(function (value, currentSelectedValues, onChange) {
        if (element.multiSelect) {
            var newValues = currentSelectedValues.includes(value)
                ? currentSelectedValues.filter(function (v) { return v !== value; })
                : __spreadArray(__spreadArray([], currentSelectedValues, true), [value], false);
            onChange(newValues);
        }
        else {
            var newValue = currentSelectedValues.includes(value) ? "" : value;
            onChange(newValue);
        }
    }, [element.multiSelect]);
    var handleVideoClick = useCallback(function (e, index) {
        e.stopPropagation();
        var video = videoRefs.current[index];
        if (!video)
            return;
        if (playingVideos.has(index)) {
            video.pause();
            setPlayingVideos(function (prev) {
                var next = new Set(prev);
                next.delete(index);
                return next;
            });
        }
        else {
            playingVideos.forEach(function (playingIndex) {
                var otherVideo = videoRefs.current[playingIndex];
                if (otherVideo) {
                    otherVideo.pause();
                }
            });
            setPlayingVideos(new Set([index]));
            video.play();
        }
    }, [playingVideos, videoRefs]);
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var fieldValue = typeof field.value === "string" ? [field.value] : field.value;
                            var minColWidth = element.minImageWidth
                                ? "".concat(element.minImageWidth, "px")
                                : "200px";
                            var maxColWidth = element.maxImageWidth
                                ? "".concat(element.maxImageWidth, "px")
                                : "1fr";
                            return (_jsxs(Box, { children: [_jsx(Box, __assign({ sx: {
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(".concat(minColWidth, ", ").concat(maxColWidth, "))"),
                                            gap: 2,
                                        } }, { children: (choicesVisible ? choices : []).map(function (choice, index) {
                                            var isSelected = fieldValue.includes(choice.value);
                                            var isChoiceInteractive = isInteractive && choicesEnabled;
                                            var isVideo = contentMode === "video" ||
                                                choice.imageLink.startsWith("data:video/");
                                            return (_jsx(Box, { children: _jsx(Card, __assign({ sx: {
                                                        position: "relative",
                                                        border: isSelected ? 2 : 1,
                                                        borderColor: isSelected
                                                            ? "primary.main"
                                                            : "divider",
                                                        borderRadius: 2,
                                                        overflow: "hidden",
                                                        opacity: !isChoiceInteractive && !isSelected ? 0.6 : 1,
                                                        minHeight: element.minImageHeight || undefined,
                                                        maxHeight: element.maxImageHeight || undefined,
                                                        minWidth: element.minImageWidth || undefined,
                                                        maxWidth: element.maxImageWidth || undefined,
                                                    } }, { children: _jsxs(CardActionArea, __assign({ onClick: function () {
                                                            return !!isChoiceInteractive &&
                                                                handleImageClick(choice.value, fieldValue, field.onChange);
                                                        }, disabled: !isChoiceInteractive && !isSelected }, { children: [isVideo ? (_jsxs(Box, __assign({ sx: {
                                                                    position: "relative",
                                                                    width: "100%",
                                                                    height: "fit-content",
                                                                    cursor: "pointer",
                                                                }, onClick: function (e) {
                                                                    return !!isInteractive &&
                                                                        handleVideoClick(e, index);
                                                                } }, { children: [_jsx(CardMedia, { component: "video", src: choice.imageLink, sx: {
                                                                            width: "100%",
                                                                            height: element.imageHeight || "auto",
                                                                            objectFit: element.imageFit || "cover",
                                                                        }, controls: false, ref: function (el) {
                                                                            if (el) {
                                                                                videoRefs.current[index] = el;
                                                                            }
                                                                            else {
                                                                                delete videoRefs.current[index];
                                                                            }
                                                                        }, onEnded: function () {
                                                                            setPlayingVideos(function (prev) {
                                                                                var next = new Set(prev);
                                                                                next.delete(index);
                                                                                return next;
                                                                            });
                                                                        } }), !playingVideos.has(index) && (_jsx(Box, __assign({ sx: {
                                                                            position: "absolute",
                                                                            top: "50%",
                                                                            left: "50%",
                                                                            transform: "translate(-50%, -50%)",
                                                                            bgcolor: "rgba(0, 0, 0, 0.5)",
                                                                            borderRadius: "50%",
                                                                            p: 1,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                        } }, { children: _jsx(PlayCircleIcon, { sx: {
                                                                                color: "white",
                                                                                fontSize: 48,
                                                                            } }) })))] }))) : (_jsx(CardMedia, { component: "img", image: choice.imageLink, alt: choice.text, sx: {
                                                                    width: "100%",
                                                                    height: element.imageHeight || "auto",
                                                                    objectFit: element.imageFit || "contain",
                                                                } })), isSelected && (_jsx(Box, __assign({ sx: {
                                                                    position: "absolute",
                                                                    top: 8,
                                                                    right: 8,
                                                                    bgcolor: "primary.main",
                                                                    borderRadius: "100%",
                                                                    p: 0.5,
                                                                    width: 24,
                                                                    height: 24,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                } }, { children: _jsx(CheckCircleIcon, { sx: { color: "white", fontSize: 24 } }) }))), element.showLabel && (_jsx(Box, __assign({ sx: {
                                                                    p: 1,
                                                                } }, { children: _jsx(Typography, __assign({ variant: "body2", align: "center" }, { children: choice.text })) })))] })) })) }, index));
                                        }) })), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 2 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default ImagePickerField;
